import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { MinusScoreIcon } from '@/assets/icons/minusScore';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import Button from '@/components/basicComponents/button';
import Tag from '@/components/basicComponents/tag';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { useResponsive } from '@/hooks/use-responsive';
import guid from '@/shared/utils/guid';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { ButtonsControl, ImportText, ImportWrapper } from '../../styles';
import { getMembershipCellValue } from '../investorImportDetail';
import { IGroupImportComparison } from '../types';

const getPrimaryEntityStatusTagProps = (status: string, isPrimaryEntity: boolean): { styleType: 'default' | 'warning' | 'valid'; title: string } => {
  switch (status) {
    case 'removed':
      return { styleType: 'warning', title: 'Primary Entity (To Replace)' };
    case 'added':
      return { styleType: 'valid', title: 'Primary Entity (New)' };
    case 'updated':
      return isPrimaryEntity ? { styleType: 'valid', title: 'Primary Entity (New)' } : { styleType: 'warning', title: 'Primary Entity (To Replace)' };
    case 'unchanged':
      return { styleType: 'default', title: 'Primary Entity (No Change)' };
  }
};

interface GroupImportDetailProps {
  currentRow: IGroupImportComparison | null;
  handleClose: () => void;
  backToTitle: string;
}

export const GroupImportDetail = ({ currentRow, handleClose, backToTitle }: GroupImportDetailProps) => {
  const { isMobile } = useResponsive();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  if (!currentRow) return <></>;

  return (
    <MainWrap>
      <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle={backToTitle} />} />
      <PageTitle title={currentRow.householdName} />
      <PaddingWrap>
        <ImportWrapper>
          <TitleWrapper>
            {currentRow.status === 'new' && (
              <>
                <ApprovedIcon width={40} height={40} fill={themeVar('context.success')} />
                <ImportText fontWeight="700" color={themeVar('context.success')}>
                  New
                </ImportText>
              </>
            )}
            {currentRow.status === 'updated' && (
              <>
                <ApprovedIcon width={40} height={40} fill={themeVar('context.success')} />
                <ImportText fontWeight="700" color={themeVar('context.success')}>
                  Updated
                </ImportText>
              </>
            )}
            {currentRow.status === 'unchanged' && (
              <>
                <MinusScoreIcon width={40} height={40} fill={themeVar('font.weak')} />
                <ImportText fontWeight="700" color={themeVar('font.weak')}>
                  No Change
                </ImportText>
              </>
            )}
            {currentRow.status === 'toDelete' && (
              <>
                <DeleteBucketIcon width={30} height={30} fill={themeVar('context.warning')} />
                <ImportText fontWeight="700" color={themeVar('context.warning')}>
                  To Delete
                </ImportText>
              </>
            )}
            {currentRow.status === 'error' && (
              <>
                <RejectedBannerIcon width={30} height={30} fill={themeVar('context.error')} />
                {currentRow?.tooltip && <ImportText fontWeight="700">{currentRow.tooltip}</ImportText>}
              </>
            )}
          </TitleWrapper>
          <DetailWrap>
            <ColumnsWrap>
              <Column isMobile={isMobile}>
                <ColumnTitle>ENTITY</ColumnTitle>
              </Column>
              <Column isMobile={isMobile}>
                <ColumnTitle>MEMBERSHIP</ColumnTitle>
              </Column>
            </ColumnsWrap>
            {currentRow.entityGroups.map((entityGroup) => {
              const primaryEntityStatusTagProps = getPrimaryEntityStatusTagProps(entityGroup.status, entityGroup.isPrimaryEntity);
              const showPrimaryEntityTag =
                entityGroup.isPrimaryEntity || (currentRow?.oldPrimaryEntityId && currentRow?.oldPrimaryEntityId === entityGroup.id);
              return (
                <React.Fragment key={guid()}>
                  <ColumnsWrap>
                    <Column isMobile={isMobile}>
                      <RowWrap>
                        <RowValue>
                          <ImportText
                            fontWeight="400"
                            fontSize="16px"
                            color={entityGroup.status === 'removed' ? themeVar('font.weak') : themeVar('font.base')}
                          >
                            {entityGroup.entityName}
                          </ImportText>
                          {showPrimaryEntityTag && (
                            <Tag title={primaryEntityStatusTagProps.title} styleType={primaryEntityStatusTagProps.styleType} size="md" />
                          )}
                        </RowValue>
                      </RowWrap>
                    </Column>
                    <Column isMobile={isMobile}>
                      <RowWrap>
                        <RowValue>
                          <ImportText fontWeight="600" fontSize="16px">
                            {getMembershipCellValue(entityGroup.status)}
                          </ImportText>
                        </RowValue>
                      </RowWrap>
                    </Column>
                  </ColumnsWrap>
                  <DividerWrapper>
                    <Divider />
                    <Divider />
                  </DividerWrapper>
                </React.Fragment>
              );
            })}
          </DetailWrap>
          <ButtonsControl>
            <Button size="lg" onClick={handleClose}>
              Done
            </Button>
          </ButtonsControl>
        </ImportWrapper>
      </PaddingWrap>
    </MainWrap>
  );
};

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 11px;
  padding-bottom: 50px;
`;

const DetailWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ColumnsWrap = styled.div`
  display: flex;
  gap: 10px;
`;

const Column = styled.div<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '100%' : '50%')};
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ColumnTitle = styled.div`
  background: ${themeVar('layer.2')};
  padding: 10px 30px;
  color: ${themeVar('font.base')};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 22.4px;
`;

const RowWrap = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 30px;
  gap: 10px;
`;

const RowValue = styled.div<{ bgColor?: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${({ bgColor }) => bgColor ?? 'transparent'};
  line-height: 18.2px;
  padding: 8px 20px;
  border-radius: 4px;
`;

const DividerWrapper = styled.div`
  display: flex;
  width: 100%;
`;

const Divider = styled.div`
  background: ${themeVar('border.base')};
  height: 1px;
  margin: 0 30px;
  width: 50%;
`;
